import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { profileAPI } from '../services/api';
import './ProfilePhotoGuard.css';

const ProfilePhotoGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    checkProfilePhoto();
  }, [location.pathname]);

  const checkProfilePhoto = async () => {
    try {
      const response = await profileAPI.getMyProfile();
      const profileData = response.data;
      setProfile(profileData);
      
      // Only encourage photo upload for female users
      const isFemale = profileData.gender === 'FEMALE';
      
      // Check if user has at least one photo
      const hasPhoto = profileData.photos && profileData.photos.length > 0;
      
      // Paths where we gently encourage a profile photo
      const protectedPaths = ['/discover', '/inbox', '/chat', '/dashboard'];
      const isProtectedPath = protectedPaths.some(path => location.pathname.startsWith(path));
      
      // Respect a recent "upload later" dismissal (re-show after 24 hours)
      const dismissedAt = localStorage.getItem('photoReminderDismissed');
      const dismissedTime = dismissedAt ? parseInt(dismissedAt) : 0;
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      const recentlyDismissed = dismissedAt && dismissedTime > oneDayAgo;
      
      // Encourage (not force) female users without a photo, unless recently dismissed
      if (isFemale && isProtectedPath && !hasPhoto && !recentlyDismissed) {
        setShowModal(true);
      } else {
        setShowModal(false);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to check profile photo:', err);
      setLoading(false);
    }
  };

  const handleUploadPhoto = () => {
    setShowModal(false);
    navigate('/profile', { state: { scrollToPhotos: true } });
  };

  const handleUploadLater = () => {
    // Remember the choice so we don't nag on every page - re-show after 24h
    localStorage.setItem('photoReminderDismissed', Date.now().toString());
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="photo-guard-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      {children}
      
      {showModal && (
        <div className="photo-guard-modal-overlay">
          <div className="photo-guard-modal">
            <div className="photo-guard-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <h2>Add a Profile Picture</h2>
            
            <p className="photo-guard-message">
              Members with a profile picture get noticed far more. Add at least one photo to stand out, get more views, and receive more messages. You can always do this later.
            </p>
            
            <div className="photo-guard-benefits">
              <div className="benefit-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Profiles with photos get 10x more views</span>
              </div>
              
              <div className="benefit-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Appear higher in search results</span>
              </div>
              
              <div className="benefit-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Receive more messages and requests</span>
              </div>
            </div>
            
            <button className="btn-upload-photo" onClick={handleUploadPhoto}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Upload Profile Picture
            </button>
            
            <button className="btn-upload-later" onClick={handleUploadLater}>
              Maybe Later
            </button>
            
            <p className="photo-guard-note">
              You can upload up to 6 photos to showcase yourself
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilePhotoGuard;
